import React, { useContext, useState } from 'react';
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import { ThemeContext } from 'styled-components';
import { useQuery, useQueryClient } from 'react-query';
import { getRuleHistory, restoreRuleHistory, saveRuleHistory, renameRuleHistory, deleteRuleHistory } from '../../../services/bill/BillRuleHistory';
import { CsrfContext } from '../../../providers/CsrfProvider';
import HistoryIcon from '@mui/icons-material/History';
import RestoreIcon from '@mui/icons-material/Restore';
import SaveIcon from '@mui/icons-material/Save';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

const BillRuleHistoryDropdown = ({ templateId, disabled = false }) => {
    const theme = useContext(ThemeContext);
    const { csrfToken } = useContext(CsrfContext);
    const queryClient = useQueryClient();
    const [anchorEl, setAnchorEl] = useState(null);
    const [dropdownEverOpened, setDropdownEverOpened] = useState(false);
    const [restoringHistoryId, setRestoringHistoryId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [dotMenuAnchorEl, setDotMenuAnchorEl] = useState(null);
    const [dotMenuEntry, setDotMenuEntry] = useState(null);
    const [renameDialogOpen, setRenameDialogOpen] = useState(false);
    const [renameDialogEntry, setRenameDialogEntry] = useState(null);
    const [renameDialogValue, setRenameDialogValue] = useState('');
    const [renaming, setRenaming] = useState(false);
    const [deletingHistoryId, setDeletingHistoryId] = useState(null);

    // Fetch history entries only when user has opened the dropdown (chosen to see the list)
    const { data: historyEntries, isLoading } = useQuery(
        ['ruleHistory', String(templateId)],
        () => getRuleHistory(String(templateId)),
        {
            enabled: !!templateId && templateId !== 'none' && dropdownEverOpened,
            refetchOnMount: false,
            refetchOnWindowFocus: false,
        }
    );

    const handleClick = (event) => {
        setDropdownEverOpened(true);
        setAnchorEl(event.currentTarget);
        queryClient.refetchQueries(['ruleHistory', String(templateId)]);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setDotMenuAnchorEl(null);
        setDotMenuEntry(null);
    };

    const handleDotMenuClick = (event, entry) => {
        event.stopPropagation();
        setDotMenuAnchorEl(event.currentTarget);
        setDotMenuEntry(entry);
    };

    const handleDotMenuClose = () => {
        setDotMenuAnchorEl(null);
        setDotMenuEntry(null);
    };

    const handleRenameClick = () => {
        if (dotMenuEntry) {
            const label = dotMenuEntry.label || dotMenuEntry.Label || '';
            setRenameDialogEntry(dotMenuEntry);
            setRenameDialogValue(label);
            setRenameDialogOpen(true);
        }
        handleDotMenuClose();
    };

    const handleRenameSubmit = async () => {
        if (!renameDialogEntry || !templateId || !csrfToken) return;
        setRenaming(true);
        try {
            await renameRuleHistory(String(templateId), renameDialogEntry.id, renameDialogValue.trim() || null, csrfToken);
            queryClient.invalidateQueries(['ruleHistory', String(templateId)]);
            setRenameDialogOpen(false);
            setRenameDialogEntry(null);
            setRenameDialogValue('');
        } catch (err) {
            console.error('Error renaming history:', err);
            alert(err.response?.data?.error || err.message || 'Failed to rename');
        } finally {
            setRenaming(false);
        }
    };

    const handleDeleteClick = () => {
        const entry = dotMenuEntry;
        if (!entry) return;
        handleDotMenuClose();
        if (!window.confirm('Delete this saved rule set? This cannot be undone.')) return;
        (async () => {
            setDeletingHistoryId(entry.id);
            try {
                await deleteRuleHistory(String(templateId), entry.id, csrfToken);
                queryClient.invalidateQueries(['ruleHistory', String(templateId)]);
            } catch (err) {
                console.error('Error deleting history:', err);
                alert(err.response?.data?.error || err.message || 'Failed to delete');
            } finally {
                setDeletingHistoryId(null);
            }
        })();
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await saveRuleHistory(String(templateId), csrfToken);
            // Invalidate queries to refresh history list
            queryClient.invalidateQueries(['ruleHistory', String(templateId)]);
        } catch (err) {
            console.error('Error saving history:', err);
            alert(err.response?.data?.error || err.message || 'Failed to save history');
        } finally {
            setSaving(false);
        }
    };

    const handleRestore = async (historyId) => {
        if (!window.confirm('Are you sure you want to restore this rule state? This will replace all current rules with the rules from this point in time.')) {
            return;
        }

        setRestoringHistoryId(historyId);
        try {
            await restoreRuleHistory(String(templateId), historyId, csrfToken);
            // Invalidate queries to refresh rules and estimated splits
            queryClient.invalidateQueries(['billRules', String(templateId)]);
            queryClient.invalidateQueries(['estimatedSplits', String(templateId)]);
            queryClient.invalidateQueries(['ruleHistory', String(templateId)]);
            handleClose();
        } catch (err) {
            console.error('Error restoring history:', err);
            alert(err.response?.data?.error || err.message || 'Failed to restore history');
        } finally {
            setRestoringHistoryId(null);
        }
    };

    const formatDate = (entry) => {
        // Try multiple date field formats to handle different response structures
        // Backend may return created_at (snake_case) or CreatedAt (PascalCase)
        let dateString = entry.created_at || entry.CreatedAt;
        
        // Fallback to state snapshot captured_at if available
        if (!dateString && entry.StateSnapshot && entry.StateSnapshot.captured_at) {
            dateString = entry.StateSnapshot.captured_at;
        }
        
        if (!dateString) {
            return 'Unknown date';
        }
        
        try {
            const date = new Date(dateString);
            // Check if date is valid
            if (isNaN(date.getTime())) {
                return 'Invalid date';
            }
            // Format as: "Dec 13, 2024, 9:53 AM"
            return date.toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
            });
        } catch (e) {
            return dateString || 'Unknown date';
        }
    };

    const getEntryDisplayText = (entry) => {
        const label = entry.label || entry.Label;
        if (label && String(label).trim()) return String(label).trim();
        return formatDate(entry);
    };

    const open = Boolean(anchorEl);

    // Separate manual saves from auto saves
    // Check both snake_case and PascalCase field names
    const manualSaves = (historyEntries || []).filter(entry => {
        const isManual = entry.is_manual_save === true || entry.IsManualSave === true;
        return isManual;
    });
    const autoSaves = (historyEntries || []).filter(entry => {
        const isManual = entry.is_manual_save === true || entry.IsManualSave === true;
        return !isManual;
    });

    return (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Button
                onClick={handleSave}
                disabled={disabled || isLoading || saving}
                sx={{
                    color: theme.colors.text,
                    minWidth: 'auto',
                    padding: '8px',
                    border: 'none',
                    '&:hover': {
                        backgroundColor: `${theme.colors.primary}10`,
                    },
                }}
                variant="text"
                size="small"
            >
                <SaveIcon />
            </Button>
            <Button
                onClick={handleClick}
                disabled={disabled || isLoading}
                sx={{
                    color: theme.colors.text,
                    minWidth: 'auto',
                    padding: '8px',
                    border: 'none',
                    '&:hover': {
                        backgroundColor: `${theme.colors.primary}10`,
                    },
                }}
                variant="text"
                size="small"
            >
                <HistoryIcon />
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                    sx: {
                        backgroundColor: theme.colors.background,
                        color: theme.colors.text,
                        maxHeight: 400,
                        minWidth: 200,
                        maxWidth: 250,
                    },
                }}
            >
                {isLoading ? (
                    <MenuItem disabled>
                        <Typography variant="body2" sx={{ color: theme.colors.text, opacity: 0.7 }}>
                            Loading history...
                        </Typography>
                    </MenuItem>
                ) : (!historyEntries || historyEntries.length === 0) && manualSaves.length === 0 && autoSaves.length === 0 ? (
                    <MenuItem disabled>
                        <Typography variant="body2" sx={{ color: theme.colors.text, opacity: 0.7 }}>
                            No history available
                        </Typography>
                    </MenuItem>
                ) : (
                    <>
                        {/* Manual saves first */}
                        {manualSaves.length > 0 && (
                            <>
                                <Box 
                                    sx={{ 
                                        py: 1.5, 
                                        px: 2,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        width: '100%',
                                    }}
                                >
                                    <Typography 
                                        variant="body2" 
                                        sx={{ 
                                            color: theme.colors.text || '#ffffff', 
                                            fontWeight: 600,
                                            fontSize: '0.875rem',
                                            textAlign: 'center',
                                        }}
                                    >
                                        manual saves
                                    </Typography>
                                </Box>
                                {manualSaves.map((entry) => (
                            <MenuItem
                                key={entry.id}
                                onClick={() => handleRestore(entry.id)}
                                disabled={restoringHistoryId === entry.id || deletingHistoryId === entry.id}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    py: 1.5,
                                    px: 1,
                                    justifyContent: 'center',
                                    textAlign: 'center',
                                    '&:hover': {
                                        backgroundColor: `${theme.colors.primary}15`,
                                    },
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center', width: '100%', mx: 'auto' }}>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => handleDotMenuClick(e, entry)}
                                        sx={{ color: theme.colors.text, padding: '4px', '&:hover': { backgroundColor: 'transparent' } }}
                                        aria-label="More options"
                                    >
                                        <MoreVertIcon fontSize="small" />
                                    </IconButton>
                                    <RestoreIcon fontSize="small" sx={{ color: theme.colors.primary, opacity: 0.7 }} />
                                    <Typography variant="body2" sx={{ color: theme.colors.text, fontWeight: 500, textAlign: 'center', flex: 1 }}>
                                        {getEntryDisplayText(entry)}
                                    </Typography>
                                </Box>
                                {restoringHistoryId === entry.id && (
                                    <Typography variant="caption" sx={{ color: theme.colors.primary, mt: 0.5, textAlign: 'center' }}>
                                        Restoring...
                                    </Typography>
                                )}
                            </MenuItem>
                            ))}
                            </>
                        )}
                        
                        {/* Divider between manual saves and auto saves - only show if both sections have items */}
                        {manualSaves.length > 0 && autoSaves.length > 0 && (
                            <Divider 
                                sx={{ 
                                    mt: 1,
                                    mb: 2,
                                    mx: 1,
                                    borderColor: theme.colors.borderContrast || 'rgba(255, 255, 255, 0.12)',
                                    borderWidth: '1px',
                                }} 
                            />
                        )}
                        
                        {/* Auto saves with label */}
                        {autoSaves.length > 0 && (
                            <>
                                <Box 
                                    sx={{ 
                                        py: 1.5, 
                                        px: 2,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        width: '100%',
                                    }}
                                >
                                    <Typography 
                                        variant="body2" 
                                        sx={{ 
                                            color: theme.colors.text || '#ffffff', 
                                            fontWeight: 600,
                                            fontSize: '0.875rem',
                                            textAlign: 'center',
                                        }}
                                    >
                                        autosaves
                                    </Typography>
                                </Box>
                                {autoSaves.map((entry) => (
                                    <MenuItem
                                        key={entry.id}
                                        onClick={() => handleRestore(entry.id)}
                                        disabled={restoringHistoryId === entry.id || deletingHistoryId === entry.id}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            py: 1.5,
                                            px: 1,
                                            justifyContent: 'center',
                                            textAlign: 'center',
                                            '&:hover': {
                                                backgroundColor: `${theme.colors.primary}15`,
                                            },
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center', width: '100%', mx: 'auto' }}>
                                            <IconButton
                                                size="small"
                                                onClick={(e) => handleDotMenuClick(e, entry)}
                                                sx={{ color: theme.colors.text, padding: '4px', '&:hover': { backgroundColor: 'transparent' } }}
                                                aria-label="More options"
                                            >
                                                <MoreVertIcon fontSize="small" />
                                            </IconButton>
                                            <RestoreIcon fontSize="small" sx={{ color: theme.colors.primary, opacity: 0.7 }} />
                                            <Typography variant="body2" sx={{ color: theme.colors.text, fontWeight: 500, textAlign: 'center', flex: 1 }}>
                                                {getEntryDisplayText(entry)}
                                            </Typography>
                                        </Box>
                                        {restoringHistoryId === entry.id && (
                                            <Typography variant="caption" sx={{ color: theme.colors.primary, mt: 0.5, textAlign: 'center' }}>
                                                Restoring...
                                            </Typography>
                                        )}
                                    </MenuItem>
                                ))}
                            </>
                        )}
                    </>
                )}
            </Menu>

            {/* Dot menu: Rename / Delete */}
            <Menu
                anchorEl={dotMenuAnchorEl}
                open={Boolean(dotMenuAnchorEl)}
                onClose={handleDotMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{
                    sx: {
                        backgroundColor: theme.colors.background,
                        color: theme.colors.text,
                        minWidth: 140,
                    },
                }}
            >
                <MenuItem onClick={handleRenameClick} sx={{ gap: 1 }}>
                    <EditIcon fontSize="small" />
                    <Typography variant="body2">Rename</Typography>
                </MenuItem>
                <MenuItem onClick={handleDeleteClick} sx={{ gap: 1 }}>
                    <DeleteIcon fontSize="small" />
                    <Typography variant="body2">Delete</Typography>
                </MenuItem>
            </Menu>

            {/* Rename dialog */}
            <Dialog
                open={renameDialogOpen}
                onClose={() => !renaming && (setRenameDialogOpen(false), setRenameDialogEntry(null), setRenameDialogValue(''))}
                PaperProps={{
                    sx: {
                        backgroundColor: theme.colors.background,
                        color: theme.colors.text,
                    },
                }}
            >
                <DialogTitle sx={{ color: theme.colors.text }}>Label history</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        margin="dense"
                        label="Label"
                        value={renameDialogValue}
                        onChange={(e) => setRenameDialogValue(e.target.value)}
                        placeholder="e.g. Before vacation"
                        variant="outlined"
                        sx={{
                            '& .MuiOutlinedInput-root': { color: theme.colors.text },
                            '& .MuiInputLabel-root': { color: theme.colors.text },
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => !renaming && (setRenameDialogOpen(false), setRenameDialogEntry(null), setRenameDialogValue(''))} disabled={renaming} sx={{ color: theme.colors.text }}>
                        Cancel
                    </Button>
                    <Button onClick={handleRenameSubmit} disabled={renaming} variant="contained">
                        {renaming ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default BillRuleHistoryDropdown;
